import { useEffect, useMemo, useState } from 'react';
import { Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faTrash } from '@fortawesome/free-solid-svg-icons';

import { Button } from '../../components/shared/FormElements';
import { ProcessType, GroupOTType } from '../../types';
import { useAppContext } from '../../Context';
import IconAwesome from '../../components/shared/IconAwesome';
import '../../styles/DataTable.css';
import { usePermissions } from '../../services';

type GroupOTProcessRelation = {
  id: number;
  sort_order: number;
  process: ProcessType;
};

type GroupOTProcessLine = {
  group_ot_line_id: number;
  line: {
    id: number;
    line_name: string;
  };
  processes: GroupOTProcessRelation[];
};

type GroupOTProcessResponse = {
  group_ot_id: number;
  group_name: string;
  lines: GroupOTProcessLine[];
};

const ViewGroupOTProcesses: React.FC<{
  groupOT: GroupOTType;
  lineId?: number;
  showLineHeader?: boolean;
  active: boolean;
}> = ({ groupOT, lineId, showLineHeader = true, active }) => {
  const [lines, setLines] = useState<GroupOTProcessLine[]>([]);
  const [availableProcesses, setAvailableProcesses] = useState<ProcessType[]>([]);
  const [selectedProcessIds, setSelectedProcessIds] = useState<Record<number, number | ''>>({});
  const [hasLoaded, setHasLoaded] = useState(false);
  const { apiCall } = useAppContext();
  const {
    canView_processgroupotline,
    canAdd_processgroupotline,
    canChange_processgroupotline,
    canDelete_processgroupotline,
  } = usePermissions([
    'view_processgroupotline',
    'add_processgroupotline',
    'change_processgroupotline',
    'delete_processgroupotline',
  ]);

  const getGroupProcesses = async () => {
    const response: GroupOTProcessResponse = await apiCall(
      'GET',
      `groups-ot/${groupOT.id}/processes/`,
      null,
      '',
      'Error al obtener los procesos del grupo OT',
    );
    if (response?.lines) {
      const filtered = lineId ? response.lines.filter((line) => line.line.id === lineId) : response.lines;
      setLines(filtered);
    } else {
      setLines([]);
    }
  };

  const getAvailableProcesses = async () => {
    const response = await apiCall('GET', 'processes/admin/', null, '', 'Error al obtener los procesos');
    if (response) {
      setAvailableProcesses(response);
    } else {
      setAvailableProcesses([]);
    }
  };

  useEffect(() => {
    if (!active || hasLoaded) return;
    getGroupProcesses();
    getAvailableProcesses();
    setHasLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, hasLoaded, groupOT.id]);

  const handleMoveProcess = async (relationId: number, direction: 'up' | 'down') => {
    const response = await apiCall(
      'POST',
      `groups-ot/${groupOT.id}/processes/${relationId}/move/`,
      { direction },
      'Orden actualizado',
      'Error al actualizar el orden',
    );
    if (response) {
      await getGroupProcesses();
    }
  };

  const handleAddProcess = async (groupOtLineId: number) => {
    const processId = selectedProcessIds[groupOtLineId];
    if (!processId) return;
    const response = await apiCall(
      'POST',
      `groups-ot/lines/${groupOtLineId}/processes/`,
      { process_id: processId },
      'Proceso agregado',
      'Error al agregar el proceso',
    );
    if (response) {
      setSelectedProcessIds((prev) => ({ ...prev, [groupOtLineId]: '' }));
      await getGroupProcesses();
    }
  };

  const handleDeleteProcess = async (groupOtLineId: number, relationId: number) => {
    const response = await apiCall(
      'DELETE',
      `groups-ot/lines/${groupOtLineId}/processes/${relationId}/`,
      null,
      'Proceso removido',
      'Error al remover el proceso',
    );
    if (response) {
      await getGroupProcesses();
    }
  };

  const availableProcessesForLine = useMemo(() => {
    const map: Record<number, ProcessType[]> = {};
    lines.forEach((line) => {
      const usedIds = new Set(line.processes.map((relation) => relation.process.id));
      map[line.group_ot_line_id] = availableProcesses.filter((process) => !usedIds.has(process.id));
    });
    return map;
  }, [lines, availableProcesses]);

  if (!canView_processgroupotline) {
    return null;
  }

  if (lines.length === 0) {
    return <p>No hay procesos configurados para esta linea</p>;
  }

  return (
    <>
      {lines.map((line) => (
        <div key={line.group_ot_line_id} style={{ marginBottom: '1rem' }}>
          {showLineHeader && <h3>{line.line.line_name}</h3>}
          <Grid container spacing={2} style={{ marginBottom: '0.75rem' }}>
            <Grid item xs={12} sm={8}>
              <select
                value={selectedProcessIds[line.group_ot_line_id] ?? ''}
                onChange={(event) =>
                  setSelectedProcessIds((prev) => ({
                    ...prev,
                    [line.group_ot_line_id]: event.target.value ? Number(event.target.value) : '',
                  }))
                }
                className="settings-input form-control"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #d7dbe0',
                  backgroundColor: '#f8f9fb',
                }}
              >
                <option value="">Selecciona un proceso</option>
                {(availableProcessesForLine[line.group_ot_line_id] || []).map((process) => (
                  <option key={process.id} value={process.id}>
                    {process.process_name}
                  </option>
                ))}
              </select>
            </Grid>
            <Grid item xs={12} sm={4}>
              {canAdd_processgroupotline && (
                <Button
                  onClick={() => handleAddProcess(line.group_ot_line_id)}
                  disabled={!selectedProcessIds[line.group_ot_line_id]}
                >
                  Agregar
                </Button>
              )}
            </Grid>
          </Grid>
          {line.processes.length === 0 ? (
            <p>
              No hay procesos asignados a esta linea.{' '}<br/>
              <small>Esta línea de producción seguirá el orden de procesos por defecto</small>
            </p>
          ) : (
            <Grid container spacing={2}>
              <div className="table-responsive">
                <TableContainer className="table-container">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <b>ICONO</b>
                        </TableCell>
                        <TableCell>
                          <b>PROCESO</b>
                        </TableCell>
                        <TableCell>
                          <b>ORDEN</b>
                        </TableCell>
                        <TableCell width={110}>
                          <b>ACCIONES</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {line.processes.map((relation, index) => {
                        const isFirst = index === 0;
                        const isLast = index === line.processes.length - 1;
                        return (
                          <TableRow key={relation.id}>
                            <TableCell>
                              {relation.process.process_icon ? (
                                <IconAwesome processIcon={relation.process.process_icon} />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>{relation.process.process_name}</TableCell>
                            <TableCell>{relation.sort_order}</TableCell>
                            <TableCell>
                              <Grid container spacing={1}>
                                <Grid item xs={4}>
                                  {canChange_processgroupotline && (
                                    <Button
                                      onClick={() => handleMoveProcess(relation.id, 'up')}
                                      disabled={isFirst}
                                    >
                                      <FontAwesomeIcon icon={faArrowUp} />
                                    </Button>
                                  )}
                                </Grid>
                                <Grid item xs={4}>
                                  {canChange_processgroupotline && (
                                    <Button
                                      onClick={() => handleMoveProcess(relation.id, 'down')}
                                      disabled={isLast}
                                    >
                                      <FontAwesomeIcon icon={faArrowDown} />
                                    </Button>
                                  )}
                                </Grid>
                                <Grid item xs={4}>
                                  {canDelete_processgroupotline && (
                                    <Button onClick={() => handleDeleteProcess(line.group_ot_line_id, relation.id)}>
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                  )}
                                </Grid>
                              </Grid>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Grid>
          )}
        </div>
      ))}
    </>
  );
};

export default ViewGroupOTProcesses;
